/* Vivre "Your fit" -- storefront glue for My Fit.
 *
 * Marks the recommended size on every size picker on the page (product page,
 * quick view, Complete the Look) from the logged-in customer's saved
 * measurements, and hosts the measurement form (inline on product pages, and
 * in a My Fit tab it adds to the Momentum Hub). It never selects a size.
 *
 * The decision itself is VivreFitCore (assets/vivre-fit-core.js), the same
 * file the chatbot sizes with. Config comes from snippets/vivre-fit.liquid.
 * Saving goes to vivre-chat /fit/profile, which writes the customer metafield
 * vivre.fit_profile. Mirrored in the vivre-chat repo; keep it ASCII.
 */
(function () {
  'use strict';

  var C = window.VivreFitConfig;
  if (!C || window.VivreFit) return;

  var core = window.VivreFitCore;
  var profile = C.profile || null;
  var LABELS = { bust: 'Bust', underbust: 'Underbust', waist: 'Waist', hip: 'Hip' };
  var HINTS = {
    bust: 'Fullest part of your chest',
    underbust: 'Directly under your bust',
    waist: 'Smallest part of your waist',
    hip: 'Fullest part of your hips'
  };
  var SIZE_NAME = /^options\[size\]$/i;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function listFields(fields) {
    var names = fields.map(function (f) { return LABELS[f].toLowerCase(); });
    return names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : names[0];
  }

  function hasProfile() {
    return !!(profile && core && core.FIELDS.some(function (f) { return profile[f]; }));
  }

  // -- The measurement form --------------------------------------------------

  function formHtml(unit) {
    var entered = (profile && profile.entered) || {};
    var sameUnit = profile && profile.unit === unit;
    var rows = core.FIELDS.map(function (f) {
      var v = '';
      if (profile && profile[f]) {
        v = sameUnit && entered[f] ? entered[f]
          : unit === 'cm' ? Math.round(profile[f] * core.CM_PER_IN * 10) / 10
          : Math.round(profile[f] * 10) / 10;
      }
      return '<label class="vf-field">'
        + '<span class="vf-field__name">' + LABELS[f] + '</span>'
        + '<span class="vf-field__hint">' + HINTS[f] + '</span>'
        + '<span class="vf-field__input"><input type="number" inputmode="decimal" step="0.1" min="0" data-field="' + f + '" value="' + esc(v) + '">'
        + '<span class="vf-field__unit">' + (unit === 'cm' ? 'cm' : 'in') + '</span></span>'
        + '<span class="vf-field__err" data-err="' + f + '"></span>'
        + '</label>';
    }).join('');

    // Not a <form>: on product pages this sits inside the theme's add-to-cart
    // form, and a nested form is dropped by the parser -- its inputs would
    // join the cart form and Enter would add to cart. Inputs carry no name
    // for the same reason.
    return '<div class="vf-form" role="group" aria-label="My measurements">'
      + '<div class="vf-form__head"><strong>My measurements</strong>'
      + '<span class="vf-units" role="group" aria-label="Units">'
      + '<button type="button" data-unit="in" class="' + (unit === 'in' ? 'is-on' : '') + '">in</button>'
      + '<button type="button" data-unit="cm" class="' + (unit === 'cm' ? 'is-on' : '') + '">cm</button>'
      + '</span></div>'
      + '<p class="vf-form__intro">Measure over your skin with the tape snug, not tight. Tops use bust and underbust; bottoms use waist and hip. Fill in what you know.</p>'
      + '<div class="vf-form__grid">' + rows + '</div>'
      + '<div class="vf-form__actions">'
      + '<button type="button" class="vf-btn vf-btn--solid" data-save>Save</button>'
      + (hasProfile() ? '<button type="button" class="vf-btn vf-btn--plain" data-clear>Clear my measurements</button>' : '')
      + '<span class="vf-form__status" aria-live="polite"></span>'
      + '</div>'
      + '<p class="vf-form__privacy">Saved to your Vivre account and only shown to you.</p>'
      + '</div>';
  }

  function save(values) {
    var body = { customerId: C.customerId, ts: C.ts, sig: C.sig };
    for (var k in values) body[k] = values[k];
    return fetch(C.api + '/fit/profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) { d.httpStatus = r.status; return d; });
    });
  }

  // Renders the form into `el`. opts.onDone() runs after a save or clear.
  function mountForm(el, opts) {
    opts = opts || {};
    if (!core) return;
    var unit = (profile && profile.unit) || 'in';

    function draw() {
      el.innerHTML = formHtml(unit);
      var form = el.querySelector('.vf-form');
      var status = form.querySelector('.vf-form__status');

      function input(f) { return form.querySelector('input[data-field="' + f + '"]'); }

      function values() {
        var v = { unit: unit };
        core.FIELDS.forEach(function (f) { v[f] = input(f).value; });
        return v;
      }

      function showErrors(errors) {
        core.FIELDS.forEach(function (f) {
          form.querySelector('[data-err="' + f + '"]').textContent = (errors && errors[f]) || '';
        });
      }

      function submit(v) {
        var built = core.buildProfile(v);
        showErrors(built.errors);
        if (built.errors) { status.textContent = ''; return; }
        status.textContent = 'Saving...';
        form.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
        save(v).then(function (d) {
          if (d.ok) {
            setProfile(d.profile);
            status.textContent = 'Saved';
            if (opts.onDone) opts.onDone();
            else setTimeout(draw, 900);
            return;
          }
          form.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
          if (d.fields) { showErrors(d.fields); status.textContent = ''; }
          else if (d.httpStatus === 401) status.textContent = 'Please refresh the page and try again.';
          else status.textContent = 'Could not save just now. Please try again.';
        }).catch(function () {
          form.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
          status.textContent = 'Could not save just now. Please try again.';
        });
      }

      form.querySelectorAll('[data-unit]').forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.getAttribute('data-unit') === unit) return;
          // Carry what's typed across the switch, converted
          var typed = values();
          unit = b.getAttribute('data-unit');
          draw();
          core.FIELDS.forEach(function (f) {
            var n = Number(typed[f]);
            if (!typed[f] || !n) return;
            var conv = unit === 'cm' ? n * core.CM_PER_IN : n / core.CM_PER_IN;
            el.querySelector('input[data-field="' + f + '"]').value = Math.round(conv * 10) / 10;
          });
        });
      });

      form.querySelector('[data-save]').addEventListener('click', function () { submit(values()); });
      form.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' || !e.target.matches('input')) return;
        e.preventDefault();          // never let Enter reach the cart form
        e.stopPropagation();
        submit(values());
      });

      var clear = form.querySelector('[data-clear]');
      if (clear) clear.addEventListener('click', function () {
        submit({ unit: unit, bust: '', underbust: '', waist: '', hip: '' });
      });
    }

    draw();
  }

  function setProfile(p) {
    profile = p || null;
    C.profile = profile;
    scanAll();
    try { document.dispatchEvent(new CustomEvent('vivre:fit:change', { detail: { profile: profile } })); } catch (e) {}
  }

  // -- Reading a picker ---------------------------------------------------------

  function productJson(container) {
    if (container._vfProduct !== undefined) return container._vfProduct;
    var el = container.querySelector('[data-product-json]');
    var p = null;
    try { p = el ? JSON.parse(el.textContent) : null; } catch (e) { p = null; }
    container._vfProduct = p;
    return p;
  }

  function sizeInputs(scope) {
    return Array.prototype.filter.call(scope.querySelectorAll('input[name]'), function (i) {
      return SIZE_NAME.test(i.name);
    });
  }

  function colourIndex(product) {
    var names = product.options || [];
    for (var i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : names[i] && names[i].name;
      if (/^colou?r$/i.test(n || '')) return i;
    }
    return -1;
  }

  function currentColour(container, product) {
    var idx = colourIndex(product);
    if (idx === -1) return null;
    var names = product.options;
    var optName = typeof names[idx] === 'string' ? names[idx] : names[idx].name;
    var form = container.querySelector('[data-product-form]') || container;

    var inputs = form.querySelectorAll('input[name]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.name !== 'options[' + optName + ']') continue;
      if (inp.type === 'radio' && inp.checked) return inp.value;
      if (inp.type !== 'radio' && inp.value) return inp.value;
    }
    // Fall back to the selected variant
    var idEl = form.querySelector('[name="id"]');
    var vid = idEl && Number(idEl.value);
    var variants = product.variants || [];
    for (var j = 0; j < variants.length; j++) {
      if (variants[j].id === vid) return variants[j].options ? variants[j].options[idx] : variants[j]['option' + (idx + 1)];
    }
    return null;
  }

  // -- Marking sizes ---------------------------------------------------------------

  function clearMarks(scope) {
    scope.querySelectorAll('.vf-rec').forEach(function (el) { el.classList.remove('vf-rec', 'vf-rec--alt'); });
    scope.querySelectorAll('.vf-badge, .vf-sr, .vf-line, .vf-inline-form').forEach(function (el) { el.parentNode.removeChild(el); });
  }

  function markEl(el, primary) {
    if (!el) return;
    el.classList.add('vf-rec');
    if (!primary) el.classList.add('vf-rec--alt');
    var b = document.createElement('span');
    b.className = 'vf-badge';
    b.setAttribute('aria-hidden', 'true');
    b.textContent = primary ? 'Your fit' : 'Or';
    el.appendChild(b);
    var sr = document.createElement('span');
    sr.className = 'vf-sr';
    sr.textContent = primary ? ' (recommended for you)' : ' (also fits you)';
    el.appendChild(sr);
  }

  function primaryOf(r) {
    if (r.status === 'size') return [r.size];
    if (r.pick) return [r.pick];
    return r.highlight.slice();
  }

  function message(r) {
    switch (r.status) {
      case 'size': {
        var s = 'Your fit: <strong>size ' + esc(r.size) + '</strong>';
        if (r.note === 'band-snug') s += '. The band will sit a little snug.';
        else if (r.note === 'band-loose') s += '. The band will sit a little relaxed.';
        else if (r.note === 'fit-to-hips') s += ', fitted to your hips. The stretchy waistband takes care of the rest.';
        else if (r.note === 'fit-to-waist') s += ', fitted to your waist. It will sit a little relaxed at the hip.';
        if (r.confirm) s += ' Add your ' + LABELS[r.confirm].toLowerCase() + ' to confirm.';
        return s;
      }
      case 'between': {
        var a = r.sizes[0], b = r.sizes[r.sizes.length - 1];
        var t = 'You\'re between <strong>size ' + esc(a) + '</strong> and <strong>size ' + esc(b) + '</strong>. ';
        if (r.lean) return t + 'This style recommends sizing ' + r.lean + ', so we\'d go ' + esc(r.pick) + '.';
        return t + 'Size up for a relaxed fit, or down for a snugger, more compressive feel.';
      }
      case 'need':
        return 'Add your ' + listFields(r.need) + ' to see your size for this piece.';
      case 'apart':
        return 'Your measurements point to quite different sizes for this piece. Worth a quick re-measure, or chat with us and we\'ll help.';
      case 'mixed':
        return 'Your measurements span <strong>sizes ' + r.highlight.map(esc).join(' and ') + '</strong> for this style. Check the size guide for the fit you prefer.';
      case 'outside':
        return 'Your measurements are outside this style\'s size range. Chat with us and we\'ll help you find a fit.';
    }
    return '';
  }

  function lineEl(html, action) {
    var line = document.createElement('div');
    line.className = 'vf-line';
    line.innerHTML = '<span class="vf-line__text">' + html + '</span>'
      + (action ? ' <button type="button" class="vf-link" data-vf-action>' + esc(action) + '</button>' : '');
    return line;
  }

  function attachFormToggle(line) {
    var btn = line.querySelector('[data-vf-action]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var next = line.nextElementSibling;
      if (next && next.classList.contains('vf-inline-form')) { next.parentNode.removeChild(next); return; }
      var box = document.createElement('div');
      box.className = 'vf-inline-form';
      line.parentNode.insertBefore(box, line.nextSibling);
      mountForm(box);
    });
  }

  // One product picker (main product or quick view)
  function evaluate(container) {
    var product = productJson(container);
    var inputs = sizeInputs(container);
    if (!product || !inputs.length) return;

    // The box layout has radios; the dropdown layout has one hidden input
    var anchorInput = inputs[0];
    var wrapper = anchorInput.closest('.selector-wrapper') || anchorInput.closest('fieldset') || anchorInput.parentNode;
    wrapper.classList.add('vf-host');   // the theme's wrapper is a no-wrap flex row
    clearMarks(wrapper);

    if (C.loggedOut) {
      if (!C.loginUrl || container.hasAttribute('data-quick-view-item')) return;
      if (!core || !core.extractChartText(product.description)) return;
      var nudge = lineEl('<a class="vf-link" href="' + esc(C.loginUrl) + '">Log in</a> to see your recommended size, based on your measurements.');
      wrapper.appendChild(nudge);
      return;
    }
    if (!core) return;

    if (!hasProfile()) {
      if (!core.extractChartText(product.description)) return;
      var add = lineEl('Get your size marked here.', 'Add my measurements');
      wrapper.appendChild(add);
      attachFormToggle(add);
      return;
    }

    var r = core.fitForProduct({
      bodyHtml: product.description,
      title: product.title,
      colour: currentColour(container, product),
      profile: profile
    });
    if (r.status === 'no-chart') return;

    var primary = primaryOf(r);
    r.highlight.forEach(function (size) {
      var isPrimary = primary.indexOf(size) !== -1;
      inputs.forEach(function (inp) {
        if (inp.value !== size) return;
        if (inp.type === 'radio') {
          var label = container.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(inp.id) : inp.id) + '"]');
          markEl(label, isPrimary);
        }
      });
      // Dropdown layout
      wrapper.querySelectorAll('.select-popout__option').forEach(function (opt) {
        if (opt.getAttribute('data-value') === size) markEl(opt, isPrimary);
      });
    });

    var line = lineEl(message(r), r.status === 'need' ? 'Add measurements' : 'Edit my measurements');
    wrapper.appendChild(line);
    attachFormToggle(line);
  }

  // -- Complete the Look ------------------------------------------------------------

  var productCache = {};
  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (p) { if (!p) delete productCache[handle]; return p; });   // retry next time
    }
    return productCache[handle];
  }

  function evaluateLookItem(item) {
    var set = item.closest('.vivre-ctlm__color-set');
    var link = item.querySelector('a[href*="/products/"]');
    var m = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    var box = item.querySelector('.vivre-ctlm__sizes');
    if (!set || !m || !box) return;
    clearMarks(item);
    if (!hasProfile()) return;
    fetchProduct(decodeURIComponent(m[1])).then(function (product) {
      if (!product) return;
      clearMarks(item);
      var r = core.fitForProduct({
        bodyHtml: product.description, title: product.title,
        colour: set.getAttribute('data-color'), profile: profile
      });
      if (!r.highlight || !r.highlight.length) return;
      var primary = primaryOf(r);
      item.querySelectorAll('.vivre-ctlm__size').forEach(function (btn) {
        var size = btn.textContent.trim();
        if (r.highlight.indexOf(size) !== -1) markEl(btn, primary.indexOf(size) !== -1);
      });
    });
  }

  function evaluateLook() {
    var overlay = document.getElementById('vivre-ctlm-overlay');
    if (!overlay || !overlay.classList.contains('is-open') || C.loggedOut || !core) return;
    overlay.querySelectorAll('.vivre-ctlm__color-set').forEach(function (set) {
      if (set.style.display === 'none') return;
      set.querySelectorAll('.vivre-ctlm__item').forEach(evaluateLookItem);
    });
  }

  // -- Momentum Hub tab -------------------------------------------------------------
  // Added from here so snippets/momentum-hub.liquid stays untouched. The hub
  // draws its tabs after loading (and redraws them on pagination), so this
  // runs whenever .mh-tabs appears without a My Fit tab. The hub's own tab
  // handlers clear .active from every .mh-tab/.mh-panel, this one included.

  function addHubTab() {
    var tabs = document.querySelector('.mh-tabs');
    if (!tabs || C.loggedOut || !core || tabs.querySelector('[data-tab="fit"]')) return;
    var btn = document.createElement('button');
    btn.className = 'mh-tab';
    btn.setAttribute('data-tab', 'fit');
    btn.textContent = 'My Fit';
    tabs.appendChild(btn);

    var panel = document.createElement('div');
    panel.id = 'mh-panel-fit';
    panel.className = 'mh-panel';
    var card = document.createElement('div');
    card.className = 'mh-card';
    panel.appendChild(card);
    tabs.parentNode.appendChild(panel);

    btn.addEventListener('click', function () {
      document.querySelectorAll('.mh-tab').forEach(function (t) { t.classList.remove('active'); });
      document.querySelectorAll('.mh-panel').forEach(function (p) { p.classList.remove('active'); });
      btn.classList.add('active');
      panel.classList.add('active');
      if (!card.firstChild) mountForm(card);
    });
  }

  // -- Wiring ---------------------------------------------------------------------

  function scanAll() {
    document.querySelectorAll('[data-product]').forEach(function (c) {
      if (c.querySelector('[data-product-json]')) evaluate(c);
    });
    evaluateLook();
  }

  function onVariantChange(e) {
    var c = e.target && e.target.closest ? e.target.closest('[data-product]') : null;
    if (c) evaluate(c); else scanAll();
  }

  var pending = null;
  function scheduleScan() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; scanAll(); }, 60);
  }

  function init() {
    scanAll();
    addHubTab();
    document.addEventListener('theme:variant:change', onVariantChange);
    // Colour swatches: re-read once the theme has applied the change
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && /^options\[/i.test(t.name) && !SIZE_NAME.test(t.name)) {
        var c = t.closest('[data-product]');
        if (c) setTimeout(function () { evaluate(c); }, 0);
      }
    });

    // Quick view and the Complete the Look modal are drawn after load
    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var rec = records[i];
          if (rec.type === 'attributes') { if (rec.target.id === 'vivre-ctlm-overlay') scheduleScan(); continue; }
          for (var j = 0; j < rec.addedNodes.length; j++) {
            var n = rec.addedNodes[j];
            if (n.nodeType === 1 && (n.matches('.mh-tabs') || n.querySelector('.mh-tabs'))) addHubTab();
            if (n.nodeType === 1 && !(n.classList && (n.classList.contains('vf-badge') || n.classList.contains('vf-sr') || n.classList.contains('vf-line') || n.classList.contains('vf-inline-form'))) &&
                (n.matches('[data-product], [data-product-json]') || n.querySelector('[data-product-json]'))) {
              scheduleScan();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  window.VivreFit = {
    mountForm: mountForm,
    refresh: scanAll,
    getProfile: function () { return profile; }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
